import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { LeadSummary } from '../types';

/**
 * Convert a lead to a Client. We default the client name to lead.name and
 * let the user edit before saving — Meta forms often submit "צבי" with no
 * surname and the firm wants to clean it up first.
 */
export function ConvertLeadModal({
  lead,
  onConverted,
  onClose,
}: {
  lead: LeadSummary;
  onConverted: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const [name, setName] = useState(lead.name?.trim() || lead.email || lead.phone || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/leads/${lead.id}/convert`, {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
      onConverted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">{t('convert.title')}</div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="field">
                <label>{t('convert.name_label')} <span className="req">*</span></label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section" style={{ color: 'var(--red)' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
              {t('convert.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
